import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from '@frontend/Components/Inputs/IconButton';
import type { DropdownElement as DropdownElementType } from '@frontend/Models/TopBar';
import './DropdownElement.css';

/**
 * DropdownElement - Navigation dropdown menu with trigger button
 * 
 * Creates a dropdown menu for navigation or actions. Includes click-outside
 * detection, keyboard navigation, and ARIA attributes for accessibility.
 * 
 * @param type - Element type identifier ('dropdown')
 * @param trigger - Trigger button configuration (icon, ariaLabel)
 * @param items - Array of menu items (label, onClick, icon)
 * @param condition - Optional conditional rendering function
 */
const DropdownElement: React.FC<DropdownElementType> = ({
  trigger,
  items,
  condition,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Toggle dropdown open/closed
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Close dropdown
  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
        // Focus the trigger button
        const triggerButton = dropdownRef.current?.querySelector('.icon-button');
        (triggerButton as HTMLButtonElement)?.focus();
        return;
      }

      // Get all menu buttons
      const menuButtons = dropdownRef.current?.querySelectorAll('.topBar-dropdown-menu-item');
      if (!menuButtons || menuButtons.length === 0) {
        return;
      }

      const currentFocus = document.activeElement;
      const currentIndex = Array.from(menuButtons).indexOf(currentFocus as HTMLButtonElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = currentIndex < menuButtons.length - 1 ? currentIndex + 1 : 0;
        (menuButtons[nextIndex] as HTMLButtonElement).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuButtons.length - 1;
        (menuButtons[prevIndex] as HTMLButtonElement).focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        (menuButtons[0] as HTMLButtonElement).focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        (menuButtons[menuButtons.length - 1] as HTMLButtonElement).focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle menu item click
  const handleItemClick = (onClick: () => void) => {
    onClick();
    closeDropdown();
  };

  // Conditional rendering - don't render if condition returns false
  if (condition && !condition()) {
    return null;
  }

  return (
    <div className="topBar-dropdown-element" ref={dropdownRef} data-testid="dropdown-element">
      <IconButton
        icon={trigger.icon}
        ariaLabel={trigger.ariaLabel}
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={isOpen}
      />
      
      {isOpen && (
        <div className="topBar-dropdown-menu" role="menu">
          {items.map((item, index) => (
            <button
              key={index}
              className="topBar-dropdown-menu-item"
              onClick={() => handleItemClick(item.onClick)}
              role="menuitem"
              tabIndex={0}
            >
              {item.icon && (
                <FontAwesomeIcon 
                  icon={item.icon} 
                  className="topBar-dropdown-menu-item-icon"
                />
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownElement;

